import {
  CREDENTIAL_TYPES,
  decide,
  type CredentialType,
  type DrawnSignatureStrokes,
  type SignatureFont,
} from '@emr/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { createOwnCredential, listOwnCredentials } from '../api/credentials.ts';
import {
  deleteMemberPhoto,
  deleteOwnPhoto,
  getMemberPhoto,
  uploadMemberPhoto,
  uploadOwnPhoto,
} from '../api/profilePhoto.ts';
import {
  getMemberSignatureLine,
  getOwnSignature,
  updateMemberSignatureLine,
  updateOwnSignature,
} from '../api/signature.ts';
import { errorMessage } from '../api/client.ts';
import { useGrants, useSession } from '../auth/sessionContext.ts';
import {
  Button,
  ErrorText,
  Field,
  PageHeading,
  Panel,
  SelectField,
} from '../components/primitives.tsx';
import { QuerySection } from '../components/QuerySection.tsx';
import { ProfilePhotoAvatar } from '../profile/ProfilePhotoAvatar.tsx';
import { OWN_PHOTO_QUERY_KEY, useOwnPhoto } from '../profile/useOwnPhoto.ts';
import { useOrgMembers } from '../schedule/useOrgMembers.ts';
import { DEFAULT_SIGNATURE_FONT, SIGNATURE_FONT_OPTIONS } from '../signature/fonts.ts';
import { SignatureCanvas } from '../signature/SignatureCanvas.tsx';
import { SignatureMark } from '../signature/SignatureMark.tsx';

const SIGNATURE_QUERY_KEY = ['profile', 'signature'] as const;

/**
 * Your own signature: draw a mark or adopt one of the bundled fonts, and see the admin-set
 * signature line that appears alongside it — on direct instruction 2026-08-18. Read-only fields and
 * mutations both go through `@emr/shared`'s schemas (`api/signature.ts`), matching every other
 * self-service surface in this app.
 *
 * Not a header menu (`shell/AppShell.tsx`'s `DateTimeFormatMenu`/`ThemeMenu` shape) — a drawing
 * canvas and a font picker need real page real estate a dropdown cannot give them, so this is a
 * routed page reached from the nav, the same shape as `AccessPage.tsx`'s "Your access".
 */
export function SignaturePage() {
  const { t } = useTranslation('signature');
  const session = useSession();
  const grants = useGrants();
  const isAdmin = decide(grants, 'org:manage_members');
  const query = useQuery({
    queryKey: SIGNATURE_QUERY_KEY,
    queryFn: ({ signal }) => getOwnSignature(signal),
  });

  return (
    <div className="flex flex-col gap-4">
      <PageHeading title={t('heading')} helpTopicId="your-signature" />
      <p className="m-0 text-sm text-ink-muted">{t('intro')}</p>

      <ProfilePhotoPanel />

      <QuerySection query={query} loadingLabel={t('loading')}>
        {(data) => (
          <>
            <Panel title={t('currentMark.panelTitle')}>
              <SignatureMark settings={data.settings} className="h-20 w-full max-w-sm" />
            </Panel>

            <MarkEditor
              // Deliberately NOT keyed on `data.updatedAt` — that was tried and found to be a real
              // bug, not a defensive improvement: `MarkEditor`'s own successful save calls
              // `queryClient.setQueryData`, which changes `data.updatedAt` on *this exact render*,
              // which would remount the component before React ever painted `save.isSuccess`,
              // silently discarding the "Saved." confirmation the same instant it should have
              // appeared. Caught live (`page.getByText('Saved.')` timing out against a real
              // browser, even though the write itself had already landed in Postgres) rather than
              // by any static check. `QuerySection` above only renders this subtree once `query`
              // has real data, so a plain, unkeyed mount already seeds `useState` correctly from
              // whatever was loaded first; this component owns its own state from then on and does
              // not need to resync from the cache at all — nothing else in this app writes another
              // person's own signature mark out from under them.
              method={data.settings.method}
              drawnStrokes={data.settings.drawnStrokes}
              fontName={data.settings.fontName}
              fontText={data.settings.fontText}
              defaultFontText={session.email.split('@')[0] ?? ''}
            />

            <Panel title={t('signatureLine.panelTitle')}>
              <p className="m-0 text-sm text-ink">
                {data.settings.signatureLine ?? (
                  <span className="text-ink-muted italic">{t('signatureLine.notSet')}</span>
                )}
              </p>
              <p className="mt-2 mb-0 text-sm text-ink-muted">{t('signatureLine.explanation')}</p>
            </Panel>
          </>
        )}
      </QuerySection>

      <CredentialsPanel />

      {isAdmin ? <AdminSignatureLineEditor /> : null}
      {isAdmin ? <AdminPhotoEditor /> : null}
    </div>
  );
}

/**
 * Self-service: the caller's own photo (`BACKLOG.md` A12). A panel of its own, not folded into the
 * mark/signature-line panels above — this is a different kind of data (a file, not text or strokes)
 * with its own query and its own upload/remove actions, and `useOwnPhoto` is shared with
 * `AppShell.tsx`'s header avatar so a change here is reflected there immediately via
 * `queryClient.setQueryData` rather than a second fetch.
 */
function ProfilePhotoPanel() {
  const { t } = useTranslation('signature');
  const session = useSession();
  const queryClient = useQueryClient();
  const photo = useOwnPhoto();

  const upload = useMutation({
    mutationFn: uploadOwnPhoto,
    onSuccess: (response) => {
      queryClient.setQueryData(OWN_PHOTO_QUERY_KEY, response);
    },
  });
  const remove = useMutation({
    mutationFn: deleteOwnPhoto,
    onSuccess: (response) => {
      queryClient.setQueryData(OWN_PHOTO_QUERY_KEY, response);
    },
  });

  return (
    <Panel title={t('photo.panelTitle')}>
      <p className="m-0 mb-3 text-sm text-ink-muted">{t('photo.explanation')}</p>

      <QuerySection query={photo} loadingLabel={t('photo.loading')}>
        {(data) => (
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <ProfilePhotoAvatar photoUrl={data.photoUrl} label={session.email} size={64} />

            <div className="flex flex-col gap-2">
              {data.photoUrl === null ? (
                <p className="m-0 text-sm text-ink-muted">{t('photo.noPhoto')}</p>
              ) : null}

              <div className="flex flex-wrap items-center gap-2">
                <label className="text-sm">
                  <span className="sr-only">{t('photo.chooseFile')}</span>
                  <input
                    type="file"
                    accept="image/jpeg,image/png"
                    onChange={(event) => {
                      const file = event.target.files?.[0];
                      event.target.value = '';
                      if (file !== undefined) upload.mutate(file);
                    }}
                    className="text-sm text-ink"
                  />
                </label>
                {data.photoUrl !== null ? (
                  <Button
                    variant="secondary"
                    pending={remove.isPending}
                    onClick={() => {
                      remove.mutate();
                    }}
                  >
                    {remove.isPending ? t('photo.removing') : t('photo.remove')}
                  </Button>
                ) : null}
                {upload.isPending ? (
                  <span className="text-sm text-ink-muted">{t('photo.uploading')}</span>
                ) : null}
                {upload.isSuccess ? (
                  <span className="text-sm text-success">{t('photo.uploaded')}</span>
                ) : null}
                {remove.isSuccess ? (
                  <span className="text-sm text-success">{t('photo.removed')}</span>
                ) : null}
              </div>
              <p className="m-0 text-xs text-ink-muted">{t('photo.hint')}</p>
              {upload.isError ? <ErrorText message={errorMessage(upload.error)} /> : null}
              {remove.isError ? <ErrorText message={errorMessage(remove.error)} /> : null}
            </div>
          </div>
        )}
      </QuerySection>
    </Panel>
  );
}

/**
 * Admin-only: set or clear a colleague's photo. Same "pick from the known-member directory rather
 * than paste a raw id" shape as `AdminSignatureLineEditor` above, and the same
 * `org:manage_members`-gated server route pair (`GET`/`POST`/`DELETE
 * /api/orgs/members/:userId/photo`).
 */
function AdminPhotoEditor() {
  const { t } = useTranslation('signature');
  const members = useOrgMembers();
  const [targetId, setTargetId] = useState('');

  const currentPhoto = useQuery({
    queryKey: ['orgs', 'members', targetId, 'photo'],
    queryFn: ({ signal }) => getMemberPhoto(targetId, signal),
    enabled: targetId !== '',
  });

  return (
    <Panel title={t('adminPhoto.panelTitle')}>
      <p className="m-0 mb-3 text-sm text-ink-muted">{t('adminPhoto.instructions')}</p>
      <div className="flex flex-col gap-3">
        {members.isPending ? (
          <p className="m-0 text-sm text-ink-muted">{t('adminPhoto.loadingMembers')}</p>
        ) : members.isError ? (
          <ErrorText message={errorMessage(members.error)} />
        ) : (
          <SelectField
            label={t('adminPhoto.memberLabel')}
            value={targetId}
            onChange={(event) => {
              setTargetId(event.target.value);
            }}
          >
            <option value="">{t('adminPhoto.chooseMemberOption')}</option>
            {members.data.map((member) => (
              <option key={member.userId} value={member.userId}>
                {member.email} ({member.role})
              </option>
            ))}
          </SelectField>
        )}

        {targetId !== '' && currentPhoto.data !== undefined ? (
          // Keyed on the target id, same "reset via key" idiom `AdminSignatureLineEditor` above uses.
          <MemberPhotoForm
            key={targetId}
            userId={targetId}
            initialPhotoUrl={currentPhoto.data.photoUrl}
          />
        ) : null}
      </div>
    </Panel>
  );
}

function MemberPhotoForm({
  userId,
  initialPhotoUrl,
}: {
  readonly userId: string;
  readonly initialPhotoUrl: string | null;
}) {
  const { t } = useTranslation('signature');
  const queryClient = useQueryClient();
  const [photoUrl, setPhotoUrl] = useState(initialPhotoUrl);

  const upload = useMutation({
    mutationFn: (file: File) => uploadMemberPhoto(userId, file),
    onSuccess: (response) => {
      setPhotoUrl(response.photoUrl);
      queryClient.setQueryData(['orgs', 'members', userId, 'photo'], response);
    },
  });
  const remove = useMutation({
    mutationFn: () => deleteMemberPhoto(userId),
    onSuccess: (response) => {
      setPhotoUrl(response.photoUrl);
      queryClient.setQueryData(['orgs', 'members', userId, 'photo'], response);
    },
  });

  return (
    <div className="flex flex-col gap-3 border-t border-row-line pt-3 sm:flex-row sm:items-center">
      <ProfilePhotoAvatar photoUrl={photoUrl} label={userId} size={48} />
      <div className="flex flex-col gap-2">
        {photoUrl === null ? (
          <p className="m-0 text-sm text-ink-muted">{t('adminPhoto.noPhoto')}</p>
        ) : null}
        <div className="flex flex-wrap items-center gap-2">
          <label className="text-sm">
            <span className="sr-only">{t('adminPhoto.chooseFile')}</span>
            <input
              type="file"
              accept="image/jpeg,image/png"
              onChange={(event) => {
                const file = event.target.files?.[0];
                event.target.value = '';
                if (file !== undefined) upload.mutate(file);
              }}
              className="text-sm text-ink"
            />
          </label>
          {photoUrl !== null ? (
            <Button
              variant="secondary"
              pending={remove.isPending}
              onClick={() => {
                remove.mutate();
              }}
            >
              {remove.isPending ? t('adminPhoto.removing') : t('adminPhoto.remove')}
            </Button>
          ) : null}
          {upload.isPending ? (
            <span className="text-sm text-ink-muted">{t('adminPhoto.uploading')}</span>
          ) : null}
          {upload.isSuccess ? (
            <span className="text-sm text-success">{t('adminPhoto.uploaded')}</span>
          ) : null}
          {remove.isSuccess ? (
            <span className="text-sm text-success">{t('adminPhoto.removed')}</span>
          ) : null}
        </div>
        {upload.isError ? <ErrorText message={errorMessage(upload.error)} /> : null}
        {remove.isError ? <ErrorText message={errorMessage(remove.error)} /> : null}
      </div>
    </div>
  );
}

const CREDENTIALS_QUERY_KEY = ['profile', 'credentials'] as const;

/**
 * Self-reported credentialing (`BACKLOG.md` C6b) — the real record `notesService.ts`'s `cosignNote`
 * checks before letting a `physician` co-sign a note, replacing the role-only proxy `C6` shipped
 * with. Lives on this page rather than a route of its own: it's the same "your own profile,
 * self-service" surface the signature mark above already is, not a separate concern.
 */
function CredentialsPanel() {
  const { t } = useTranslation('signature');
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: CREDENTIALS_QUERY_KEY,
    queryFn: ({ signal }) => listOwnCredentials(signal),
  });

  const [credentialType, setCredentialType] = useState<CredentialType>('MD');
  const [licenseNumber, setLicenseNumber] = useState('');
  const [licenseState, setLicenseState] = useState('');
  const [expiresAt, setExpiresAt] = useState('');

  const add = useMutation({
    mutationFn: () =>
      createOwnCredential({
        credentialType,
        licenseNumber: licenseNumber.trim().length > 0 ? licenseNumber.trim() : null,
        licenseState: licenseState.trim().length > 0 ? licenseState.trim() : null,
        status: 'active',
        issuedAt: null,
        expiresAt: expiresAt.trim().length > 0 ? expiresAt.trim() : null,
      }),
    onSuccess: (credential) => {
      queryClient.setQueryData(CREDENTIALS_QUERY_KEY, (previous: typeof query.data) => [
        ...(previous ?? []),
        credential,
      ]);
      setLicenseNumber('');
      setLicenseState('');
      setExpiresAt('');
    },
  });

  return (
    <Panel title={t('credentials.panelTitle')}>
      <p className="m-0 text-sm text-ink-muted">{t('credentials.explanation')}</p>

      <QuerySection query={query} loadingLabel={t('loading')}>
        {(credentials) =>
          credentials.length === 0 ? (
            <p className="mt-3 mb-0 text-sm text-ink-muted">{t('credentials.emptyMessage')}</p>
          ) : (
            <div className="mt-3 overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-line text-ink-muted">
                    <th className="py-1.5 pr-3 font-medium">
                      {t('credentials.columns.credentialType')}
                    </th>
                    <th className="py-1.5 pr-3 font-medium">
                      {t('credentials.columns.licenseNumber')}
                    </th>
                    <th className="py-1.5 pr-3 font-medium">
                      {t('credentials.columns.licenseState')}
                    </th>
                    <th className="py-1.5 pr-3 font-medium">{t('credentials.columns.status')}</th>
                    <th className="py-1.5 font-medium">{t('credentials.columns.expiresAt')}</th>
                  </tr>
                </thead>
                <tbody>
                  {credentials.map((credential) => (
                    <tr key={credential.id} className="border-b border-row-line last:border-0">
                      <td className="py-1.5 pr-3 text-ink">{credential.credentialType}</td>
                      <td className="py-1.5 pr-3 text-ink-muted break-words [overflow-wrap:anywhere]">
                        {credential.licenseNumber ?? '—'}
                      </td>
                      <td className="py-1.5 pr-3 text-ink-muted">
                        {credential.licenseState ?? '—'}
                      </td>
                      <td className="py-1.5 pr-3 text-ink-muted">{credential.status}</td>
                      <td className="py-1.5 text-ink-muted">{credential.expiresAt ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        }
      </QuerySection>

      <form
        onSubmit={(event) => {
          event.preventDefault();
          add.mutate();
        }}
        className="mt-4 flex flex-col gap-3 border-t border-row-line pt-4 sm:flex-row sm:flex-wrap sm:items-end"
      >
        <SelectField
          label={t('credentials.typeLabel')}
          value={credentialType}
          onChange={(event) => {
            setCredentialType(event.target.value as CredentialType);
          }}
        >
          {CREDENTIAL_TYPES.map((type) => (
            <option key={type} value={type}>
              {type}
            </option>
          ))}
        </SelectField>
        <Field
          label={t('credentials.licenseNumberLabel')}
          value={licenseNumber}
          onChange={(event) => {
            setLicenseNumber(event.target.value);
          }}
          maxLength={100}
          className="sm:max-w-[16ch]"
        />
        <Field
          label={t('credentials.licenseStateLabel')}
          value={licenseState}
          onChange={(event) => {
            setLicenseState(event.target.value);
          }}
          maxLength={50}
          className="sm:max-w-[12ch]"
        />
        <Field
          label={t('credentials.expiresAtLabel')}
          type="date"
          value={expiresAt}
          onChange={(event) => {
            setExpiresAt(event.target.value);
          }}
          className="sm:max-w-[16ch]"
        />
        <Button type="submit" pending={add.isPending}>
          {t('credentials.addButton')}
        </Button>
      </form>
      {add.isError ? <ErrorText message={errorMessage(add.error)} /> : null}
      <p className="mt-3 mb-0 text-xs text-ink-muted">{t('credentials.selfReportNotice')}</p>
    </Panel>
  );
}

type Tab = 'drawn' | 'font';

function MarkEditor({
  method,
  drawnStrokes,
  fontName,
  fontText,
  defaultFontText,
}: {
  readonly method: 'drawn' | 'font' | null;
  readonly drawnStrokes: DrawnSignatureStrokes | null;
  readonly fontName: SignatureFont | null;
  readonly fontText: string | null;
  readonly defaultFontText: string;
}) {
  const { t } = useTranslation('signature');
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<Tab>(method ?? 'drawn');
  const [strokes, setStrokes] = useState<DrawnSignatureStrokes>(drawnStrokes ?? []);
  const [selectedFont, setSelectedFont] = useState<SignatureFont>(
    fontName ?? DEFAULT_SIGNATURE_FONT,
  );
  const [text, setText] = useState(fontText ?? defaultFontText);

  const save = useMutation({
    mutationFn: () =>
      tab === 'drawn'
        ? updateOwnSignature({ method: 'drawn', drawnStrokes: strokes })
        : updateOwnSignature({ method: 'font', fontName: selectedFont, fontText: text }),
    onSuccess: (response) => {
      queryClient.setQueryData(SIGNATURE_QUERY_KEY, response);
    },
  });

  const canSave = tab === 'drawn' ? strokes.length > 0 : text.trim().length > 0;

  return (
    <Panel title={t('markEditor.panelTitle')}>
      <div className="mb-3 flex gap-2" role="tablist" aria-label={t('markEditor.tabsAriaLabel')}>
        <TabButton
          active={tab === 'drawn'}
          onClick={() => {
            setTab('drawn');
          }}
        >
          {t('markEditor.drawTab')}
        </TabButton>
        <TabButton
          active={tab === 'font'}
          onClick={() => {
            setTab('font');
          }}
        >
          {t('markEditor.fontTab')}
        </TabButton>
      </div>

      {tab === 'drawn' ? (
        <SignatureCanvas value={strokes} onChange={setStrokes} />
      ) : (
        <div className="flex flex-col gap-3">
          <Field
            label={t('markEditor.nameToRender')}
            value={text}
            onChange={(event) => {
              setText(event.target.value);
            }}
            maxLength={100}
          />
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {SIGNATURE_FONT_OPTIONS.map((option) => (
              <button
                key={option.id}
                type="button"
                onClick={() => {
                  setSelectedFont(option.id);
                }}
                aria-pressed={selectedFont === option.id}
                className={`rounded-control border p-3 text-left transition-colors ${
                  selectedFont === option.id
                    ? 'border-accent-text bg-accent-soft'
                    : 'border-line bg-surface hover:bg-raised'
                }`}
              >
                <span className="mb-1 block text-xs font-medium text-ink-muted">
                  {option.label}
                </span>
                <span
                  className="block text-2xl text-ink [overflow-wrap:anywhere]"
                  style={{ fontFamily: option.cssFontFamily }}
                >
                  {text.trim().length > 0 ? text : option.label}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="mt-3 flex items-center gap-3">
        <Button
          pending={save.isPending}
          disabled={!canSave}
          onClick={() => {
            save.mutate();
          }}
        >
          {t('markEditor.save')}
        </Button>
        {save.isSuccess ? (
          <span className="text-sm text-success">{t('markEditor.saved')}</span>
        ) : null}
      </div>
      {save.isError ? <ErrorText message={errorMessage(save.error)} /> : null}
    </Panel>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  readonly active: boolean;
  readonly onClick: () => void;
  readonly children: string;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={`rounded-control px-3 py-1.5 text-sm font-medium ${
        active
          ? 'bg-accent text-accent-ink'
          : 'border border-line bg-surface text-ink hover:bg-raised'
      }`}
    >
      {children}
    </button>
  );
}

/**
 * Admin-only: edit a colleague's signature line. Picked from the same org-member directory
 * (`useOrgMembers`) the scheduling UI's provider picker uses, rather than pasting and confirming a
 * raw user id the way this page's own first version did — a dropdown of known-valid colleagues
 * can't name someone outside the practice, so there is nothing left to confirm once one is picked.
 */
function AdminSignatureLineEditor() {
  const { t } = useTranslation('signature');
  const members = useOrgMembers();
  const [targetId, setTargetId] = useState('');

  const currentLine = useQuery({
    queryKey: ['orgs', 'members', targetId, 'signature-line'],
    queryFn: ({ signal }) => getMemberSignatureLine(targetId, signal),
    enabled: targetId !== '',
  });

  return (
    <Panel title={t('admin.panelTitle')}>
      <p className="m-0 mb-3 text-sm text-ink-muted">{t('admin.instructions')}</p>
      <div className="flex flex-col gap-3">
        {members.isPending ? (
          <p className="m-0 text-sm text-ink-muted">{t('admin.loadingMembers')}</p>
        ) : members.isError ? (
          <ErrorText message={errorMessage(members.error)} />
        ) : (
          <SelectField
            label={t('admin.memberLabel')}
            value={targetId}
            onChange={(event) => {
              setTargetId(event.target.value);
            }}
          >
            <option value="">{t('admin.chooseMemberOption')}</option>
            {members.data.map((member) => (
              <option key={member.userId} value={member.userId}>
                {member.email} ({member.role})
              </option>
            ))}
          </SelectField>
        )}

        {targetId !== '' && currentLine.data !== undefined ? (
          // Keyed on the target id: a fresh instance (and therefore a fresh `useState` initial
          // value seeded from `currentLine.data`) every time the admin picks a different
          // colleague, with no effect-based sync needed — the same "reset via key" idiom
          // `MarkEditor` above uses.
          <SignatureLineForm
            key={targetId}
            userId={targetId}
            initialLine={currentLine.data.signatureLine}
          />
        ) : null}
      </div>
    </Panel>
  );
}

function SignatureLineForm({
  userId,
  initialLine,
}: {
  readonly userId: string;
  readonly initialLine: string | null;
}) {
  const { t } = useTranslation('signature');
  const [line, setLine] = useState(initialLine ?? '');
  const save = useMutation({
    mutationFn: () =>
      updateMemberSignatureLine(userId, line.trim().length > 0 ? line.trim() : null),
  });

  return (
    <>
      <Field
        label={t('lineForm.label')}
        value={line}
        onChange={(event) => {
          setLine(event.target.value);
        }}
        maxLength={200}
        hint={t('lineForm.hint')}
      />
      <div className="flex items-center gap-3">
        <Button
          pending={save.isPending}
          onClick={() => {
            save.mutate();
          }}
        >
          {t('lineForm.save')}
        </Button>
        {save.isSuccess ? (
          <span className="text-sm text-success">{t('lineForm.saved')}</span>
        ) : null}
      </div>
      {save.isError ? <ErrorText message={errorMessage(save.error)} /> : null}
    </>
  );
}
